import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronRight, Plus, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectGroups } from "../../../features/members/members-slice";
import {
  createGroup,
  fetchGroups,
} from "../../../features/members/slice-extensions/members-slice-extension";
import { Group } from "../../../features/members/models/Group";
import { ROUTES } from "../../../routes";

interface GroupEnrollmentFieldProps {
  selectedGroups: Group[];
  onChange: (groups: Group[]) => void;
  disabled?: boolean;
}

/**
 * Group enrollment for the member form: search-as-you-type over the existing
 * groups, inline "create '<name>'" when nothing matches, and removable chips
 * for the selection. Loads the group list itself.
 */
const GroupEnrollmentField: React.FC<GroupEnrollmentFieldProps> = ({
  selectedGroups, onChange, disabled,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const allGroups = useAppSelector(selectGroups);

  const [searchText, setSearchText] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dispatch(fetchGroups());
  }, [dispatch]);

  // Close on outside click, matching the house popover behavior.
  useEffect(() => {
    if (!showDropdown) return;
    const close = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [showDropdown]);

  const filteredGroups = useMemo(() => {
    const selectedIds = new Set(selectedGroups.map((g) => g.id));
    return allGroups.filter((group) => {
      if (selectedIds.has(group.id)) return false;
      if (!searchText.trim()) return true;
      return group.name.toLowerCase().includes(searchText.toLowerCase());
    });
  }, [allGroups, selectedGroups, searchText]);

  const canCreate = useMemo(() => {
    const trimmed = searchText.trim();
    if (!trimmed) return false;
    return !allGroups.some((g) => g.name.toLowerCase() === trimmed.toLowerCase());
  }, [searchText, allGroups]);

  const selectGroup = (group: Group) => {
    onChange([...selectedGroups, group]);
    setSearchText("");
    setShowDropdown(false);
  };

  const createAndSelect = async () => {
    const name = searchText.trim();
    if (!name || isCreating) return;
    setIsCreating(true);
    try {
      const result = await dispatch(createGroup({ name, description: null, tags: [] }));
      if (createGroup.fulfilled.match(result)) {
        selectGroup(result.payload);
        toast.success(t("members.groupCreatedNamed", { name }));
        dispatch(fetchGroups());
      } else {
        toast.error(t("groups.errors.createFailed"));
      }
    } catch {
      toast.error(t("groups.errors.createFailed"));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-grey-30 dark:text-grey-70 text-sm">
        {t("members.addGroupsDescription")}
      </p>

      <div className="relative" ref={containerRef}>
        <label className="block text-sm font-medium text-grey-30 dark:text-white mb-1">
          {t("members.addGroupsOptional")}:
        </label>
        <input
          type="text"
          className="input"
          placeholder={t("members.searchGroups")}
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          disabled={disabled}
        />

        {showDropdown && (filteredGroups.length > 0 || canCreate) && (
          <div className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-xl border border-grey-90 bg-white p-1 shadow-lg dark:border-primary-20 dark:bg-primary-10">
            {filteredGroups.map((group) => (
              <button
                key={group.id}
                type="button"
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-grey-10 hover:bg-grey-95 dark:text-grey-90 dark:hover:bg-primary-20"
                onClick={() => selectGroup(group)}
              >
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary-80 dark:bg-primary-30 text-primary-20 dark:text-primary-90 text-xs font-semibold shrink-0 leading-none">
                  {group.name.substring(0, 2).toUpperCase()}
                </span>
                <span className="text-sm leading-none">{group.name}</span>
              </button>
            ))}
            {canCreate && (
              <button
                type="button"
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-primary-40 hover:bg-grey-95 dark:text-primary-90 dark:hover:bg-primary-20"
                onClick={createAndSelect}
                disabled={isCreating}
              >
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary-90 dark:bg-primary-30 text-primary-40 dark:text-primary-90 shrink-0">
                  <Plus size={16} strokeWidth={2.5} />
                </span>
                <span className="text-sm leading-none">
                  {isCreating
                    ? t("common.creating")
                    : t("members.createNamedGroup", { name: searchText.trim() })}
                </span>
              </button>
            )}
          </div>
        )}
      </div>

      {selectedGroups.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedGroups.map((group) => (
            <div
              key={group.id}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-90 dark:bg-primary-20 text-primary-30 dark:text-primary-90 text-sm font-medium"
            >
              <span>{group.name}</span>
              <button
                type="button"
                onClick={() => onChange(selectedGroups.filter((g) => g.id !== group.id))}
                className="ml-1 text-primary-40 dark:text-primary-80 hover:text-primary-20 dark:hover:text-white"
                disabled={disabled}
                aria-label={t("common.remove", "Remove")}
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => navigate(ROUTES.GROUPS)}
        className="inline-flex items-center gap-1 text-primary-40 dark:text-primary-90 text-sm hover:underline"
      >
        <span>{t("members.showAllGroups")}</span>
        <ChevronRight size={14} />
      </button>
    </div>
  );
};

export default GroupEnrollmentField;
